import React, { useState } from 'react';
import { PencilSquare, Trash } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getDesktopDateParts,
  getJournalChips,
  getJournalCompletion,
  getJournalDateRaw,
  getJournalPnl,
  getNotesPreview,
  getTickerFromJournal,
  journalPnlTone,
  journalPurpleBorderStyle,
} from './journalListUtils';

const VISIBLE_CHIPS = 4;

export default function TradeJournalDesktopCard({
  journal,
  accountName,
  formatJournalDate,
  onOpen,
  onEdit,
  onDeleteRequest,
  onTickerClick,
}) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);

  const ticker = getTickerFromJournal(journal);
  const dateRaw = getJournalDateRaw(journal);
  const { time, date } = getDesktopDateParts(dateRaw);
  const pnl = getJournalPnl(journal);
  const notesPreview = getNotesPreview(journal);
  const completion = getJournalCompletion(journal);
  const chips = getJournalChips(journal, { formatJournalDate }).filter(
    (c) => !pnl || c.label !== pnl.label
  );
  const visibleChips = chips.slice(0, VISIBLE_CHIPS);
  const hiddenChipCount = Math.max(0, chips.length - VISIBLE_CHIPS);
  const up = colors.chartUp || '#07CBA3';
  const completionStyles = {
    complete: { bg: `${up}26`, color: up },
    progress: { bg: 'rgba(147, 197, 253, 0.15)', color: '#93C5FD' },
    started: { bg: 'rgba(251, 146, 60, 0.15)', color: '#FB923C' },
    draft: { bg: 'rgba(148, 163, 184, 0.12)', color: '#94A3B8' },
  };
  const statusStyle = completionStyles[completion.tone] || completionStyles.draft;
  const pnlTone = journalPnlTone(colors, pnl?.numeric);
  const cardBg = hovered ? colors.bgSecondary : colors.bgPrimary;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(journal)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onOpen(journal);
        }
      }}
      role="button"
      tabIndex={0}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '12px 16px',
        padding: '14px 16px',
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: hovered ? '0 4px 20px rgba(0,0,0,0.18)' : 'none',
        ...journalPurpleBorderStyle(colors, cardBg, { strong: hovered }),
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {onTickerClick && ticker ? (
              <button
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onTickerClick(ticker, ev);
                }}
                title={`Open ${ticker} on Trade`}
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.02em',
                  color: colors.primary,
                  padding: '4px 10px',
                  background: `${colors.primary}18`,
                  borderRadius: 6,
                  flexShrink: 0,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {ticker}
              </button>
            ) : (
              <span
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.02em',
                  color: colors.primary,
                  padding: '4px 10px',
                  background: `${colors.primary}18`,
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              >
                {ticker}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text }}>
                {date}
              </div>
              <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: 2 }}>
                {time}
                {accountName ? ` · ${accountName}` : ''}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
            {pnl && (
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 999,
                  backgroundColor: pnlTone.backgroundColor,
                  color: pnlTone.color,
                  whiteSpace: 'nowrap',
                }}
              >
                {pnl.label} · {pnl.formatted}
              </span>
            )}
            <span
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                backgroundColor: statusStyle.bg,
                color: statusStyle.color,
                flexShrink: 0,
              }}
            >
              {completion.label}
              {completion.percent < 100 && completion.percent > 0 ? ` · ${completion.percent}%` : ''}
            </span>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          {visibleChips.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: notesPreview ? 8 : 0 }}>
              {visibleChips.map((c, i) => (
                <span
                  key={`${c.label}-${i}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 8,
                    fontSize: '0.75rem',
                    maxWidth: '100%',
                    ...journalPurpleBorderStyle(colors, colors.bgSecondary),
                  }}
                >
                  <span style={{ color: colors.textMuted, flexShrink: 0 }}>{c.label}:</span>
                  <span
                    style={{
                      color: colors.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.value}
                  </span>
                </span>
              ))}
              {hiddenChipCount > 0 && (
                <span style={{ fontSize: '0.75rem', color: colors.textMuted, alignSelf: 'center' }}>
                  +{hiddenChipCount} more
                </span>
              )}
            </div>
          )}
          {notesPreview && (
            <p
              style={{
                margin: 0,
                fontSize: '0.8rem',
                color: colors.textLight,
                lineHeight: 1.45,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {notesPreview}
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 8,
          opacity: hovered ? 1 : 0.35,
          transition: 'opacity 0.15s ease',
        }}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            onClick={() => onEdit(journal)}
            title="Edit journal"
            aria-label="Edit journal"
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.bgSecondary,
              color: colors.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PencilSquare size={15} />
          </button>
          <button
            type="button"
            onClick={() => onDeleteRequest(journal.id)}
            title="Delete journal"
            aria-label="Delete journal"
            style={{
              width: 32,
              height: 32,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.bgSecondary,
              color: colors.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Trash size={15} />
          </button>
        </div>
      </div>
    </article>
  );
}
